import { Box, Button, CircularProgress, IconButton, LinearProgress, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ProductGlyph from "./icons/ProductGlyph";
import IntegrationIcons from "./icons/IntegrationIcons";
import { DRAFT_BLUE, DraftBadge, draftDisplayName } from "./DraftProductCard";
import type { Product } from "./types";
import type { ProductCreationDraftRecord } from "../newProduct/draftApi";
import { productCreationDraftProgress } from "../newProduct/draftApi";
import ContextMeshScore from "./ContextMeshScore";
import ProductBatchGenerationStatus, { isActiveProductBatch } from "./ProductBatchGenerationStatus";
import type { TrackedBatch } from "../batchGeneration";
import AnalyzeContextButton from "./AnalyzeContextButton";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

interface ProductsListViewProps {
    products: Product[];
    onOpenProduct?: (product: Product) => void;
    onEditProduct?: (product: Product) => void;
    onDeleteProduct?: (product: Product) => void;
    onSyncProduct?: (product: Product) => void;
    onGenerateAll?: (product: Product) => void;
    onAnalyzeContext?: (product: Product) => void;
    onRetryBuild?: (product: Product) => void;
    retryingProductIds?: ReadonlySet<string>;
    retryErrorsByProductId?: ReadonlyMap<string, string>;
    onOpenBatchDetails?: () => void;
    batchesByProductId?: ReadonlyMap<string, TrackedBatch>;
    pendingBatchProductIds?: ReadonlySet<string>;
    onCreateProduct?: () => void;
    createProductLabel?: string;
    createProductDescription?: string;
    drafts?: ProductCreationDraftRecord[];
    onResumeDraft?: (draft: ProductCreationDraftRecord) => void;
    onDeleteDraft?: (draft: ProductCreationDraftRecord) => void;
}

const headerCellSx = {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    px: 0.5
};

const activeBuildActivity = (product: Product) => {
    const activities = product.build?.activities ?? [];
    return (
        activities.find(activity => activity.status === "running") ??
        activities.find(activity => activity.status === "queued" || activity.status === "not_started") ??
        activities.find(activity => activity.status === "failed") ??
        activities[activities.length - 1]
    );
};

const ProductsListView = ({
    products,
    onOpenProduct,
    onEditProduct,
    onDeleteProduct,
    onSyncProduct,
    onGenerateAll,
    onAnalyzeContext,
    onRetryBuild,
    retryingProductIds,
    retryErrorsByProductId,
    onOpenBatchDetails,
    batchesByProductId,
    pendingBatchProductIds,
    onCreateProduct,
    createProductLabel = "Build a new Product",
    createProductDescription = "Work with our Agentic agent to organize and build your next amazing product.",
    drafts = [],
    onResumeDraft,
    onDeleteDraft
}: ProductsListViewProps) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const muted = theme.palette.text.secondary;
    const actionsWidth = 72 + (onSyncProduct ? 36 : 0) + (onGenerateAll ? 36 : 0) + (onRetryBuild ? 96 : 0);

    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                background: theme.palette.background.paper,
                overflowX: "auto",
                "& > .MuiStack-root": { minWidth: 1040 }
            }}
        >
            {/* Header row */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                    px: 1.75,
                    py: 1.25,
                    background: theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: muted
                }}
            >
                <Box sx={{ flex: "0 0 24px" }} />
                <Box sx={{ flex: 1, ...headerCellSx }}>Product</Box>
                <Box sx={{ flex: "0 0 140px", ...headerCellSx }}>Updated</Box>
                <Box sx={{ flex: "0 0 110px", textAlign: "right", ...headerCellSx }}>Features</Box>
                <Box sx={{ flex: "0 0 110px", textAlign: "right", ...headerCellSx }}>Stories</Box>
                <Box sx={{ flex: "0 0 144px", textAlign: "center", ...headerCellSx }}>Mesh</Box>
                <Box sx={{ flex: "0 0 130px", ...headerCellSx }}>Integrations</Box>
                <Box sx={{ flex: `0 0 ${actionsWidth}px` }} />
            </Stack>

            {/* Build a new Product row */}
            {onCreateProduct && <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                role="button"
                tabIndex={0}
                onClick={onCreateProduct}
                onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onCreateProduct();
                    }
                }}
                sx={{
                    cursor: "pointer",
                    px: 1.75,
                    py: 1.75,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(90deg, ${alpha(primary, 0.06)} 0%, ${alpha(primary, 0.015)} 70%, transparent 100%)`,
                    transition: "background .15s",
                    "&:hover": {
                        background: `linear-gradient(90deg, ${alpha(primary, 0.12)} 0%, ${alpha(primary, 0.04)} 70%, ${alpha(primary, 0.02)} 100%)`
                    }
                }}
            >
                <Box sx={{ flex: "0 0 24px", display: "flex", justifyContent: "center" }}>
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 0.75,
                            background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.dark} 100%)`,
                            color: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 2px 6px ${alpha(primary, 0.35)}`
                        }}
                    >
                        <AutoAwesomeIcon sx={{ fontSize: 13 }} />
                    </Box>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: primary }}>{createProductLabel}</Typography>
                    <Typography
                        sx={{
                            fontSize: 12.5,
                            color: muted,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {` · ${createProductDescription}`}
                    </Typography>
                </Stack>
                <Box sx={{ flex: `0 0 ${actionsWidth}px` }} />
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: "0 0 auto" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: primary }}>Start</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 13, color: primary }} />
                </Stack>
            </Stack>}

            {/* Draft rows */}
            {onResumeDraft &&
                onDeleteDraft &&
                drafts.map(draft => {
                    const progress = productCreationDraftProgress(draft);
                    return (
                        <Stack
                            key={`draft-${draft.draft_id}`}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            role="button"
                            tabIndex={0}
                            onClick={() => onResumeDraft(draft)}
                            onKeyDown={event => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onResumeDraft(draft);
                                }
                            }}
                            sx={{
                                cursor: "pointer",
                                px: 1.75,
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                background: DRAFT_BLUE.wash,
                                transition: "background .12s",
                                "&:hover": {
                                    background: DRAFT_BLUE.soft
                                }
                            }}
                        >
                            <Box sx={{ flex: "0 0 24px", display: "flex", justifyContent: "center" }}>
                                <Box
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: 0.75,
                                        backgroundColor: DRAFT_BLUE.soft,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    <EditNoteOutlinedIcon sx={{ fontSize: 14, color: DRAFT_BLUE.main }} />
                                </Box>
                            </Box>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: 13,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {draftDisplayName(draft)}
                                </Typography>
                                <DraftBadge />
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: muted,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    · {progress.completed} of {progress.total} sections complete
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    flex: "0 0 140px",
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                    color: muted,
                                    px: 0.5
                                }}
                            >
                                {new Date(draft.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                            </Box>
                            <Box sx={{ flex: "0 0 110px" }} />
                            <Box sx={{ flex: "0 0 110px" }} />
                            <Box sx={{ flex: "0 0 144px" }} />
                            <Box sx={{ flex: "0 0 130px", px: 0.5 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: DRAFT_BLUE.main }}>Continue creating</Typography>
                                    <ArrowForwardIcon sx={{ fontSize: 12, color: DRAFT_BLUE.main }} />
                                </Stack>
                            </Box>
                            <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ flex: `0 0 ${actionsWidth}px`, color: muted }}>
                                <Tooltip title="Delete draft" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={event => {
                                            event.stopPropagation();
                                            onDeleteDraft(draft);
                                        }}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            color: muted,
                                            "&:hover": {
                                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                                                color: theme.palette.error.main
                                            }
                                        }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    );
                })}

            {/* Product rows */}
            {products.map((product, idx) => {
                const batch = batchesByProductId?.get(product.id);
                const batchPending = pendingBatchProductIds?.has(product.id) ?? false;
                const batchActive = isActiveProductBatch(batch);
                const generationDisabled = product.cardState === "processing" || batchPending || batchActive;
                const generationTooltip =
                    product.cardState === "processing"
                        ? "Available when product processing finishes"
                        : batchPending
                          ? "Checking Context Mesh requirements"
                          : batchActive
                            ? "Batch generation is already in progress"
                            : "Generate All Work Products";
                const canAnalyzeContext = product.contextMeshStatus === "unavailable"
                    && (product.build == null || product.build.state === "completed")
                    && onAnalyzeContext != null;
                const canRetryBuild = product.build?.state === "failed" && product.build.retryable === true && onRetryBuild != null;
                const retryPending = retryingProductIds?.has(product.id) ?? false;
                const retryError = retryErrorsByProductId?.get(product.id);
                const buildFailed = product.build?.state === "failed";
                return (
                    <Stack
                        key={product.id}
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        role={onOpenProduct ? "button" : undefined}
                        aria-label={onOpenProduct ? (product.cardState === "processing" ? `View build progress for ${product.name}` : `Open ${product.name}`) : undefined}
                        tabIndex={onOpenProduct ? 0 : undefined}
                        onClick={onOpenProduct ? () => onOpenProduct(product) : undefined}
                        onKeyDown={event => {
                            if (onOpenProduct && (event.key === "Enter" || event.key === " ")) {
                                event.preventDefault();
                                onOpenProduct(product);
                            }
                        }}
                        sx={{
                            cursor: onOpenProduct ? "pointer" : "default",
                            px: 1.75,
                            py: 1.5,
                            borderBottom: idx < products.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                            transition: "background .12s",
                            ...(onOpenProduct
                                ? {
                                      "&:hover": {
                                          background: alpha(primary, 0.04)
                                      }
                                  }
                                : {})
                        }}
                    >
                        <Box sx={{ flex: "0 0 24px", display: "flex", justifyContent: "center" }}>
                            <ProductGlyph color={product.color} size={16} />
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    fontSize: 13,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {product.name}
                            </Typography>
                            <Typography
                                role={retryError ? "alert" : undefined}
                                sx={{
                                    fontSize: 12,
                                    color: muted,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {product.cardState === "processing"
                                    ? `· ${retryError || activeBuildActivity(product)?.label || "Building product context"}`
                                    : `· ${product.sub}`}
                            </Typography>
                            {product.cardState === "processing" && (
                                <Box
                                    sx={{
                                        px: 0.7,
                                        py: 0.2,
                                        borderRadius: 4,
                                        bgcolor: alpha(buildFailed ? theme.palette.error.main : primary, 0.1),
                                        color: buildFailed ? theme.palette.error.main : primary,
                                        fontSize: 9.5,
                                        fontWeight: 800,
                                        textTransform: "uppercase"
                                    }}
                                >
                                    {buildFailed ? "Failed" : "Processing"}
                                </Box>
                            )}
                        </Stack>
                        <Box
                            sx={{
                                flex: "0 0 140px",
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 12,
                                color: muted,
                                px: 0.5
                            }}
                        >
                            {batch || batchPending ? (
                                <ProductBatchGenerationStatus
                                    productName={product.name}
                                    batch={batch}
                                    checking={batchPending}
                                    compact
                                    onOpenDetails={onOpenBatchDetails}
                                />
                            ) : product.cardState === "processing" ? (
                                activeBuildActivity(product)?.progress != null ? (
                                    `${activeBuildActivity(product)?.progress}%`
                                ) : activeBuildActivity(product)?.status === "failed" ? (
                                    "Attention"
                                ) : (
                                    "Building"
                                )
                            ) : (
                                product.updated
                            )}
                            {!batch && !batchPending && product.cardState === "processing" && activeBuildActivity(product)?.status !== "failed" && (
                                <LinearProgress
                                    variant={activeBuildActivity(product)?.progress == null ? "indeterminate" : "determinate"}
                                    value={activeBuildActivity(product)?.progress ?? undefined}
                                    sx={{ mt: 0.6, height: 3, borderRadius: 2 }}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                flex: "0 0 110px",
                                textAlign: "right",
                                fontFamily: '"JetBrains Mono", monospace',
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                fontSize: 13,
                                px: 0.5
                            }}
                        >
                            {product.cardState === "processing" ? "—" : product.features}
                        </Box>
                        <Box
                            sx={{
                                flex: "0 0 110px",
                                textAlign: "right",
                                fontFamily: '"JetBrains Mono", monospace',
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                fontSize: 13,
                                px: 0.5
                            }}
                        >
                            {product.cardState === "processing" ? "—" : product.stories}
                        </Box>
                        <Box sx={{ flex: "0 0 144px", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, px: 0.5 }}>
                            <ContextMeshScore analysis={product.contextMeshAnalysis} score={product.contextMeshScore} status={product.contextMeshStatus} buildState={product.build?.state} compact />
                            {canAnalyzeContext && (
                                <AnalyzeContextButton compact productName={product.name} onAnalyze={() => onAnalyzeContext?.(product)} />
                            )}
                        </Box>
                        <Box sx={{ flex: "0 0 130px", px: 0.5 }}>
                            {product.cardState !== "processing" && <IntegrationIcons ids={product.integrations} size={14} />}
                        </Box>
                        <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ flex: `0 0 ${actionsWidth}px`, color: muted }}>
                            {canRetryBuild && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={retryPending}
                                    startIcon={retryPending ? <CircularProgress size={13} color="inherit" /> : <ReplayRoundedIcon fontSize="small" />}
                                    aria-label={`Retry product build for ${product.name}`}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onRetryBuild?.(product);
                                    }}
                                    sx={{ minWidth: 88, px: 1, fontSize: 11, fontWeight: 700 }}
                                >
                                    {retryPending ? "Retrying…" : "Retry"}
                                </Button>
                            )}
                            {onGenerateAll && (
                                <Tooltip title={generationTooltip} arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label={`Generate all work products for ${product.name}`}
                                            disabled={generationDisabled}
                                            onClick={event => {
                                                event.stopPropagation();
                                                onGenerateAll(product);
                                            }}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                color: primary,
                                                "&:hover": {
                                                    backgroundColor: alpha(primary, 0.09)
                                                }
                                            }}
                                        >
                                            <AutoAwesomeMotionOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {onSyncProduct && (
                                <Tooltip title={product.cardState === "processing" ? "Available when processing finishes" : "Sync to Jira/DevOps"} arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="Sync product"
                                            disabled={product.cardState === "processing"}
                                            onClick={event => {
                                                event.stopPropagation();
                                                onSyncProduct(product);
                                            }}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                color: muted,
                                                "&:hover": {
                                                    backgroundColor: alpha(primary, 0.07),
                                                    color: primary
                                                }
                                            }}
                                        >
                                            <SyncOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {onEditProduct && <Tooltip title={product.cardState === "processing" ? "Available when processing finishes" : "Edit product"} arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Edit product"
                                        disabled={product.cardState === "processing"}
                                        onClick={event => {
                                            event.stopPropagation();
                                            onEditProduct(product);
                                        }}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            color: muted,
                                            "&:hover": {
                                                backgroundColor: alpha(primary, 0.07),
                                                color: primary
                                            }
                                        }}
                                    >
                                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>}
                            {onDeleteProduct && <Tooltip title="Delete product" arrow>
                                <IconButton
                                    size="small"
                                    onClick={event => {
                                        event.stopPropagation();
                                        onDeleteProduct(product);
                                    }}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        color: muted,
                                        "&:hover": {
                                            backgroundColor: alpha(theme.palette.error.main, 0.08),
                                            color: theme.palette.error.main
                                        }
                                    }}
                                >
                                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>}
                        </Stack>
                    </Stack>
                );
            })}
        </Box>
    );
};

export default ProductsListView;
